import {useCallback} from "react";
import {useNavigate} from "react-router-dom";

import {type LiveDataEntryKind, liveDataEntryKind, RevaLifeCycleState} from "@reva/shared/lifecycle-state";

import {
    selectIsNewestSnapshotLoaded,
    selectIsSnapshot,
    selectLifeCycleState,
    selectRevaId,
} from "../redux/persistenceSlice";
import {fetchPersistence} from "../redux/persistenceThunk";
import {resetAll} from "../redux/resetThunk";
import {useRevaDispatch, useRevaSelector} from "../redux/revaHooks";
import {PERMISSION_RULE_CORRECTION, PERMISSION_RULE_FINALIZATION} from "../util/permissions";
import {buildValuationId} from "../util/valuationId";
import {useCheckPermission} from "./permissionHooks";

/**
 * Handlers to switch the loaded data state of the current valuation.
 *
 * Both first discard the client-side session (reset) and then load the target: a snapshot id
 * (see {@link buildValuationId}) opens the frozen snapshot, the bare revaId returns to the
 * live (editable) data state. App.tsx then reflects the loaded id in the URL.
 */
export function useLoadValuationState() {
    const dispatch = useRevaDispatch();
    const revaId = useRevaSelector(selectRevaId);

    const resetAndLoad = useCallback(
        async (id: string | null) => {
            if (!id) return;
            dispatch(resetAll());
            await dispatch(fetchPersistence({id}));
        },
        [dispatch],
    );

    /** "Load data state": open the frozen snapshot `snapshotNumber` of the current valuation. */
    const loadSnapshot = useCallback(
        (snapshotNumber: number) => resetAndLoad(revaId ? buildValuationId(revaId, snapshotNumber) : null),
        [resetAndLoad, revaId],
    );

    /** "Load current data state": leave the snapshot view and return to the live valuation. */
    const loadLiveData = useCallback(() => resetAndLoad(revaId), [resetAndLoad, revaId]);

    return {loadSnapshot, loadLiveData};
}

/**
 * Unload the current valuation and go to `to`.
 *
 * Reset and navigation are one transition and have to be issued from the same tick. Between them
 * the store holds no valuation while the page being left is still mounted, because a data router
 * runs the target's loaders before it pushes the history entry - the URL does not change on the
 * navigate() below. The effects of that page which reconcile the URL with the store stand down
 * while a navigation is pending (see useNavigationPending), and that only covers them if the
 * navigation is already under way when they run. Resetting first and navigating later, across an
 * await, reopens exactly that gap.
 * @return handler that unloads and navigates, resolving once the navigation has settled
 */
export function useUnloadValuation(): (to: string) => Promise<void> {
    const dispatch = useRevaDispatch();
    const navigate = useNavigate();

    return useCallback(
        async (to: string) => {
            dispatch(resetAll());
            await navigate(to);
        },
        [dispatch, navigate],
    );
}

/** i18n key of the "continue" action label per live data entry kind. */
const LIVE_DATA_ENTRY_LABEL_KEY: Record<LiveDataEntryKind, string> = {
    dataEntry: "object.snapshotNavContinue",
    correction: "object.snapshotNavContinueCorrection",
};

/**
 * Whether the current user may load the live (editable) data state while viewing a snapshot,
 * and the i18n key of the matching "continue" action label — `null` when it is not available.
 *
 * Shared by the snapshot banner and the lifecycle menu so both apply the same rules
 * (see {@link liveDataEntryKind}).
 */
export function useLiveDataEntry(): {kind: LiveDataEntryKind; labelKey: string} | null {
    const lifeCycleState = useRevaSelector(selectLifeCycleState);
    const canFinalize = useCheckPermission(PERMISSION_RULE_FINALIZATION);
    const kind = liveDataEntryKind(lifeCycleState, {canFinalize});
    return kind ? {kind, labelKey: LIVE_DATA_ENTRY_LABEL_KEY[kind]} : null;
}

/**
 * Whether the current user may start a correction while viewing a snapshot: only on the newest
 * snapshot of a finalized valuation. Mirrors the snapshot banner's "Korrektur starten" link and
 * is shared with the lifecycle menu so both stay in sync.
 */
export function useCanStartCorrection(): boolean {
    const lifeCycleState = useRevaSelector(selectLifeCycleState);
    const isSnapshot = useRevaSelector(selectIsSnapshot);
    const isNewestSnapshot = useRevaSelector(selectIsNewestSnapshotLoaded);
    const canCorrect = useCheckPermission(PERMISSION_RULE_CORRECTION);
    return canCorrect && isSnapshot && isNewestSnapshot && lifeCycleState === RevaLifeCycleState.Final;
}
